/**
 * One opponent, drawn where they actually sit.
 *
 * North faces you across the felt, so their rack runs left to right. West and
 * East sit side-on, so their tiles are drawn a quarter turn round, the way you
 * would really see them from your chair. A seat owns the things that belong to
 * a *person*: the name plate, the concealed rack standing in front of them, and
 * the sets they have exposed. What belongs to the *table* - their discards -
 * lives inside the wall, in the discard field, because that is where it really
 * lies.
 *
 * The name plate is turned back towards the camera, so a seat can sit on a
 * tilted surface and still be read at a glance.
 *
 * A concealed hand is drawn from a *count* only. This component never receives
 * an opponent's tile kinds - in Online play the server has already removed
 * them - so no orientation or layout choice here can leak information.
 */

import { useI18n } from "../i18n";
import type { SeatSide, TableSeat } from "../game/tableModel";
import { MeldRow } from "./MeldRow";
import { TileBack } from "./tiles";

export interface SeatPanelProps {
  seat: TableSeat;
  name: string;
  side: SeatSide;
  isTurn: boolean;
  waiting: boolean;
  isDealer: boolean;
}

/** A rack never shows more backs than a full hand, however the count arrives. */
const MAX_RACK = 14;

export function SeatPanel({ seat, name, side, isTurn, waiting, isDealer }: SeatPanelProps) {
  const { t, seat: seatWind } = useI18n();
  const concealed = seat.handCount + (seat.hasDrawnTile ? 1 : 0);
  const sideOn = side === "east" || side === "west";

  return (
    <section
      className={`seat seat--${side} ${isTurn ? "seat--turn" : ""} ${waiting ? "seat--waiting" : ""}`}
      data-seat={seat.id}
      data-side={side}
      data-testid={`seat-${side}`}
    >
      <div className="seat__body">
        <header className="seat__plate">
          <span className="seat__wind-chip" aria-hidden="true">
            {seatWind(seat.seat).slice(0, 1)}
          </span>
          <h3 className="seat__name">
            {name} <span className="seat__wind">{seatWind(seat.seat)}</span>
          </h3>
          <span className="seat__badges">
            {isDealer ? <span className="badge badge--dealer">{t("seat.dealerBadge")}</span> : null}
            {isTurn ? <span className="badge">{t("seat.turnBadge")}</span> : null}
            {waiting ? <span className="badge badge--claim">{t("seat.claimBadge")}</span> : null}
          </span>
          <span className="seat__count" aria-hidden="true">
            {concealed}
          </span>
        </header>

        <div className="seat__rack" aria-label={t("seat.concealed", { count: concealed })}>
          {Array.from({ length: Math.min(concealed, MAX_RACK) }, (_, index) => (
            <TileBack key={index} size="xs" orientation={sideOn ? "side" : "up"} index={index} />
          ))}
        </div>
      </div>

      {seat.melds.length > 0 ? (
        <div className="seat__melds">
          <MeldRow melds={seat.melds} size="xs" />
        </div>
      ) : null}
    </section>
  );
}
